/**
 * PiChatDock — "Pi chat on all tabs" P1 (2026-07-03).
 *
 * The ONE Pi/legacy chat instance, hoisted OUT of AppShell (where it was
 * Designer-only) and rendered exactly once at the App level as a sibling of
 * TriShellLayout. Presented as an always-mounted collapsible dock on the right
 * edge of the content region, so the chat is reachable on EVERY view (Designer
 * / Knowledge / Terminals / Modules / Deploys) — mirroring how the voice
 * "BRIDGE" is present everywhere.
 *
 * INLINE (not overlay): the dock is a flex SIBLING of the shell content, so the
 * content reflows to leave room for it — it never occludes the shell (an
 * absolute overlay hid the Designer preview's right edge; fixed live 2026-07-03).
 * Because all five shells live inside the single TriShellLayout flex item,
 * reserving the dock's width in the App-level flex row reflows every view
 * uniformly with ZERO per-shell layout surgery.
 *
 * WHY a single instance (NOT per-shell replication like voice): the Pi chat's
 * live streaming state lives INSIDE the <pi-chat-panel> web component, not in a
 * store (AppShell.tsx:78-82). Five per-shell copies would be five instances
 * with split state, and switching tabs mid-stream would swap to a different one.
 * So there is exactly one <ChatPanel> here, and it is NEVER unmounted on
 * open/close — collapse only toggles CSS width/display.
 *
 * `none` harness fully hides the dock + its toggle (no empty rail). Mobile is
 * deferred (P3): App renders this only on desktop (!isTouch), so it never
 * coexists with the touch Chat pane's instance.
 */
import { MessageSquare, X } from 'lucide-react';
import { ChatPanel } from './ChatPanel';
import { useChatHarnessStore } from '@/stores/chatHarnessStore';
import { PI_DOCK_WIDTH_RANGE, usePiDockStore } from '@/stores/piDockStore';
import { useChatTabsStore } from '@/stores/chatTabsStore';
import { useTenantStore } from '@/stores/tenantStore';
import {
  GHL_THEMES,
  agentThemeVars,
  loadGhlThemeId,
  type GhlThemeId,
} from '@/shared/theme/agentThemes';
// Scoped overrides that recolor pi-web-ui's BAKED-literal Tailwind utilities
// (`.bg-background{background-color:rgb(10 10 11 …)}` — CSS-var overrides alone
// don't touch it). Scoped to `.pi-agent-themed` so only the dock is affected.
import './PiChatDock.agentTheme.css';

export function PiChatDock() {
  const harness = useChatHarnessStore((s) => s.harness);
  const open = usePiDockStore((s) => s.open);
  const width = usePiDockStore((s) => s.width);
  const setOpen = usePiDockStore((s) => s.setOpen);
  const setWidth = usePiDockStore((s) => s.setWidth);

  // Per-tab GHL theme (operator 2026-07-04). The active Pi chat tab's themeId
  // drives the shadcn CSS tokens the chat renders against; undefined falls back
  // to the tenant default. Legacy harness (no tabs) uses the tenant default.
  const tenantId = useTenantStore((s) => s.tenantId);
  const tabs = useChatTabsStore((s) => s.tabs);
  const activeTabId = useChatTabsStore((s) => s.activeTabId);
  const setTabTheme = useChatTabsStore((s) => s.setTabTheme);
  const activeTab = tabs.find((t) => t.id === activeTabId);
  const themeId: GhlThemeId = activeTab?.themeId ?? loadGhlThemeId(tenantId);
  const themeVars = agentThemeVars(GHL_THEMES[themeId]);

  // Drag the LEFT edge to widen the dock (the dock is anchored to the right, so
  // moving the grip left — clientX decreasing — grows it). setWidth clamps to
  // [min, viewport − MIN_LEFT_PANELS_WIDTH], so the left panels keep a floor.
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = usePiDockStore.getState().width;
    const onMove = (ev: MouseEvent) => {
      setWidth(startWidth + (startX - ev.clientX));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  // 'none' harness → no dock, no toggle, no chat. Nothing to keep mounted (the
  // 'none' path has no <pi-chat-panel> stream to preserve).
  if (harness === 'none') return null;

  return (
    <div data-testid="pi-chat-dock" className="flex h-full shrink-0">
      {/* Left-edge resize grip (only when open). Drag to widen; double-click to
          reset to the default width. */}
      {open && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize Pi chat"
          data-testid="pi-chat-dock-resize"
          onMouseDown={startResize}
          onDoubleClick={() => setWidth(PI_DOCK_WIDTH_RANGE.default)}
          title="Drag to resize — double-click to reset"
          className="h-full w-1 shrink-0 cursor-col-resize bg-vibe-border transition-colors hover:bg-vibe-accent"
        />
      )}
      {/* Collapsed rail: a thin vertical strip to re-open. Only when closed. It
          reserves ~36px so it does NOT occlude the shell content either. */}
      {!open && (
        <button
          type="button"
          data-testid="pi-chat-dock-toggle"
          aria-label="Open Pi chat"
          title="Open Pi chat"
          onClick={() => setOpen(true)}
          className="flex h-full w-9 shrink-0 flex-col items-center gap-2 border-l border-vibe-border bg-vibe-bg-secondary py-3 text-vibe-text-secondary hover:text-vibe-text"
        >
          <MessageSquare className="h-4 w-4" />
          <span className="text-[10px] tracking-wide [writing-mode:vertical-rl]">Pi&nbsp;Chat</span>
        </button>
      )}

      {/* The panel — ALWAYS mounted (collapse toggles CSS only, never unmount)
          so an in-flight Pi stream survives open/close. When open it is a real
          layout column of `width` px; the sibling shell content reflows. */}
      <div
        data-testid="pi-chat-dock-panel"
        // themeVars drive pi-web-ui's shadcn tokens (var(--background)/--card/
        // --accent/…) so the chat surface takes the active tab's GHL theme.
        style={{ ...(themeVars as React.CSSProperties), width: open ? width : 0, display: open ? 'flex' : 'none' }}
        className="pi-agent-themed h-full shrink-0 flex-col overflow-hidden border-l border-vibe-border bg-vibe-bg-secondary"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-vibe-border px-3 py-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-vibe-text-secondary">
            <MessageSquare className="h-3.5 w-3.5" />
            Pi Chat
          </span>
          <div className="flex items-center gap-2">
            {/* Per-tab theme picker (operator 2026-07-04). Shown when a Pi tab
                is active; sets that tab's GHL theme. */}
            {activeTab && tenantId && (
              <select
                value={themeId}
                onChange={(e) => setTabTheme(tenantId, activeTab.id, e.target.value as GhlThemeId)}
                data-testid="pi-dock-theme-picker"
                aria-label="Chat theme"
                title="Theme for this chat tab"
                className="rounded border border-vibe-border bg-vibe-bg px-1.5 py-0.5 text-[11px] text-vibe-text-secondary hover:text-vibe-text"
              >
                {Object.values(GHL_THEMES).map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              data-testid="pi-chat-dock-collapse"
              aria-label="Collapse Pi chat"
              title="Collapse Pi chat"
              onClick={() => setOpen(false)}
              className="text-vibe-text-secondary hover:text-vibe-text"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <ChatPanel />
        </div>
      </div>
    </div>
  );
}

export default PiChatDock;
